//IMPORTANT: Keep this enum in sync with the TimePeriod enum in the backend
export enum TimePeriod {
  OneHour = "1H",
  TwoHours = "2H",
  TwentyFourHours = "24H",
  FortyEightHours = "48H",
  SeventyTwoHours = "72H",
  OneDay = "1D",
  OneWeek = "1W",
  OneMonth = "1M",
  ThreeMonths = "3M",
  SixMonths = "6M",
  OneYear = "1Y",
  AllTime = "ALL",
}

// need to keep this in sync with backend
export enum ExpirationTimePeriod {
  QuarterHour = "15m",
  HalfHour = "30m",
  OneHour = "1H",
  TwoHours = "2H",
  ThreeHours = "3H",
  SixHours = "6H",
  TwelveHours = "12H",
  TwentyFourHours = "24H",
}

// need to keep this in sync with backend
export enum UserPermissions {
  All = 0,
  ApplicationAdministrator = 1,
  KomatsuEmployee = 2,
  ParentCompany = 3,
  Minesite = 4,
  ModifiedDistributorCustomer = 5,
}

// Filtering by OS Types
export enum OSType {
  ALL = "ALL",
  UWP = "UWP",
  UWPSL = "UWPSL",
  IOS = "IOS",
  ANDROID = "ANDROID",
}

// Keep this in sync with the permission enum in the backend

export namespace UserPermissionsUtils {
  export function getStringForPermission(permission: UserPermissions): string {
    switch (permission) {
      case UserPermissions.All:
        return "All";
      case UserPermissions.ApplicationAdministrator:
        return "Application Administrator";
      case UserPermissions.KomatsuEmployee:
        return "Komatsu Employee";
      case UserPermissions.ParentCompany:
        return "Parent Company";
      case UserPermissions.Minesite:
        return "Minesite";
      case UserPermissions.ModifiedDistributorCustomer:
        return "Modified Distributor/Customer";
      default:
        throw Error("Permission is not recognized: " + permission);
    }
  }
}

export namespace TimePeriodUtils {
  export function getStringForTimePeriod(timePeriod: TimePeriod): string {
    switch (timePeriod) {
      case TimePeriod.OneHour:
        return "1 hour";
      case TimePeriod.TwoHours:
        return "2 hours";
      case TimePeriod.TwentyFourHours:
        return "24 hours";
      case TimePeriod.FortyEightHours:
        return "48 hours";
      case TimePeriod.SeventyTwoHours:
        return "72 hours";
      case TimePeriod.OneDay:
        return "1 day";
      case TimePeriod.OneWeek:
        return "1 week";
      case TimePeriod.OneMonth:
        return "1 month";
      case TimePeriod.ThreeMonths:
        return "3 months";
      case TimePeriod.SixMonths:
        return "6 months";
      case TimePeriod.OneYear:
        return "1 year";
      case TimePeriod.AllTime:
        return "All Time";
      default:
        throw Error("TimePeriod is not recognized: " + timePeriod);
    }
  }

  export function getStringForExpirationTimePeriod(
    expirationTimePeriod: ExpirationTimePeriod
  ): string {
    switch (expirationTimePeriod) {
      case ExpirationTimePeriod.QuarterHour:
        return "15 minutes";
      case ExpirationTimePeriod.HalfHour:
        return "30 minutes";
      case ExpirationTimePeriod.OneHour:
        return "1 hour";
      case ExpirationTimePeriod.TwoHours:
        return "2 hours";
      case ExpirationTimePeriod.ThreeHours:
        return "3 hours";
      case ExpirationTimePeriod.SixHours:
        return "6 hours";
      case ExpirationTimePeriod.TwelveHours:
        return "12 hours";
      case ExpirationTimePeriod.TwentyFourHours:
        return "24 hours";
      default:
        throw Error(
          "ExpirationTimePeriod is not recognized: " + ExpirationTimePeriod
        );
    }
  }

  // I should be able to use the other functions if I don't make it expirationTimePeriod and leave it as timePeriod, right?

  export function addTimePeriodToDate(
    timePeriod: TimePeriod,
    date: Date
  ): Date {
    let hours = date.getHours();
    let day = date.getDate();
    let month = date.getMonth();
    let year = date.getFullYear();

    switch (timePeriod) {
      case TimePeriod.OneHour:
        hours = hours + 1;
        break;
      case TimePeriod.TwoHours:
        hours = hours + 2;
        break;
      case TimePeriod.TwentyFourHours:
      case TimePeriod.OneDay:
        day = day + 1;
        break;
      case TimePeriod.FortyEightHours:
        day = day + 2;
        break;
      case TimePeriod.SeventyTwoHours:
        day = day + 3;
        break;
      case TimePeriod.OneWeek:
        day = day + 7;
        break;
      case TimePeriod.OneMonth:
        month = month + 1;
        break;
      case TimePeriod.ThreeMonths:
        month = month + 3;
        break;
      case TimePeriod.SixMonths:
        month = month + 6;
        break;
      case TimePeriod.OneYear:
        year = year + 1;
        break;
      case TimePeriod.AllTime:
        year = year + 24; // arbitrary number big enough
      default:
        throw new Error("TimePeriod not recognized.");
    }

    if (hours > 23) {
      hours = hours - 23;
      day = day + 1;
    }

    if (month > 11) {
      month = month - 11;
      year = year + 1;
    }

    while (day > getMaxDaysForMonth(month, year)) {
      let maxDaysForMonth = getMaxDaysForMonth(month, year);

      if (
        timePeriod == TimePeriod.OneMonth ||
        TimePeriod.ThreeMonths ||
        TimePeriod.SixMonths ||
        TimePeriod.OneYear
      ) {
        day = maxDaysForMonth;
      } else {
        day = day - maxDaysForMonth;
        month = month + 1;
        if (month > 11) {
          month = month - 11;
          year = year + 1;
        }
      }
    }

    return new Date(year, month, day, hours);
  }

  export function subtractTimePeriodFromDate(
    timePeriod: TimePeriod,
    date: Date
  ): Date {
    let hours = date.getHours();
    let day = date.getDate();
    let month = date.getMonth();
    let year = date.getFullYear();

    switch (timePeriod) {
      case TimePeriod.OneHour:
        hours = hours - 1;
        break;
      case TimePeriod.TwoHours:
        hours = hours - 2;
        break;
      case TimePeriod.TwentyFourHours:
      case TimePeriod.OneDay:
        day = day - 1;
        break;
      case TimePeriod.FortyEightHours:
        day = day - 2;
        break;
      case TimePeriod.SeventyTwoHours:
        day = day - 3;
        break;
      case TimePeriod.OneWeek:
        day = day - 7;
        break;
      case TimePeriod.OneMonth:
        month = month - 1;
        break;
      case TimePeriod.ThreeMonths:
        month = month - 3;
        break;
      case TimePeriod.SixMonths:
        month = month - 6;
        break;
      case TimePeriod.OneYear:
        year = year - 1;
        break;
      case TimePeriod.AllTime:
        year = year - 24; // arbitrary number small enough
        break;
      default:
        throw new Error("TimePeriod not recognized.");
    }

    if (hours < 0) {
      hours = hours + 23;
      day = day - 1;
    }

    if (month < 0) {
      month = month + 11;
      year = year - 1;
    }

    while (day > getMaxDaysForMonth(month, year)) {
      let maxDaysForMonth = getMaxDaysForMonth(month, year);

      if (
        timePeriod == TimePeriod.OneMonth ||
        TimePeriod.ThreeMonths ||
        TimePeriod.SixMonths ||
        TimePeriod.OneYear
      ) {
        day = maxDaysForMonth;
      } else {
        day = day - maxDaysForMonth;
        month = month + 1;
        if (month > 11) {
          month = month - 11;
          year = year + 1;
        }
      }
    }

    return new Date(year, month, day, hours);
  }

  function getMaxDaysForMonth(month: number, year: number): number {
    switch (month) {
      case 0:
      case 2:
      case 4:
      case 6:
      case 7:
      case 9:
      case 11:
        return 31;
      case 1:
        return isLeapYear(year) ? 29 : 28;
      case 3:
      case 5:
      case 8:
      case 10:
        return 30;
      default:
        throw Error("Not an acceptable date.");
    }
  }

  function isLeapYear(year: number): boolean {
    if (year % 400 === 0) {
      return true;
    } else if (year % 100 === 0) {
      return false;
    } else if (year % 4 === 0) {
      return true;
    } else {
      return false;
    }
  }
}

export class AnalyticsFilter {
  public Pkid = 0;
  public ApplyFilter: boolean = true;
  public OrderBy: string;
  public IsAscending: boolean = false;
  public FilterByStartDate: Date | null; // downloaded manuals
  public FilterByOpenedDate: Date | null; // manualsopen
  public FilterByClosedDate: Date | null; // manualsopen
  public FilterByFileName: string; // documents (updated/created manuals)
  public FilterByProductName: string; // documents (updated/created manuals)
  public Period: string;
  public ExpirationPeriod: string;
  public FilterByEndDate: Date | null; // downloaded manuals
  public FilterByUserid: number | null;
  public FilterByDocumentid: number | null;
  public FilterByAppVerionid: number | null; // userlog
  public FilterByProductDescription: string; // created manuals
  public FilterByFmiLanguage: string; // created manuals
  public FilterByDivision: string; // created manuals
  public FilterByAbleToPrint: string; // created manuals
  public FilterByNotAbleToPrint: string; // created manuals
  public FilterByCreatedBy: string; // created manuals
  public FilterByDate: Date | null; // documents printed and annotations sent
  public FilterBySearchTerm: string; // search term used data
  public FilterByInactiveUser: boolean; // users analytics
  public FilterByActiveuser: boolean; // users analytics
  public FilterByName: string; // users analytics
  public FilterByAllEmployees: string; // users analytics
  public FilterBySelectedCompanies: number[]; // users analytics
  public FilterBySelectedMinesites: number[]; // users analytics
  public FilterBySelectedPermissions: number[]; // users analytics
  public FilterByOsType: string; // app os analytics
  public FilterByLoggedInDate: Date | null; // userlogs (filters if loggedIn date is greater than this value)
  public FilterByLoggedOutDate: Date | null; // userLogs (filters if loggedOut date is greater than this value)
  public FilterByCurrentlyLoggedIn: boolean; // userLogs
  public FilterByMinimum: number;
  public FilterByMaximum: number;
  public LimitResults: number | null;
}
